import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useMySchoolMembership, useSchoolEnrollmentRequests, useManageEnrollmentRequest, useSchoolCohorts } from '@/hooks/useSchoolApi';
import { useAuth } from '@/contexts/AuthContext';

export default function JoinRequestsQueue() {
  const { user } = useAuth();
  const { data: membership } = useMySchoolMembership();
  const schoolId = (membership as any)?.schools?.id || membership?.school_id;
  const { data: requests, isLoading } = useSchoolEnrollmentRequests(schoolId);
  const { data: cohorts } = useSchoolCohorts(schoolId);
  const manageMutation = useManageEnrollmentRequest();

  const isAdmin = membership?.role === 'school_admin';

  // Instructor sees only requests for their cohorts.
  // FIX-R38-HIGH-2: null-safe filter — `cohorts` may be undefined while the
  // useSchoolCohorts query is loading and `r.cohort_id` may be null for
  // school-wide requests. Without the explicit `r.cohort_id` guard, the
  // `cohorts.find(...)` call would short-circuit to undefined and the
  // request would be hidden from the instructor (silently). With the
  // explicit guard, we render an empty list while loading and only filter
  // once cohorts have arrived.
  // PRODUCT-NOTE: instructor visibility scoped by `created_by` only — when
  // multi-instructor cohort assignment lands, this filter must check the
  // future `cohort_instructors` junction table.
  const filteredRequests = isAdmin
    ? requests
    : (cohorts && requests
        ? requests.filter((r) => {
            if (!r.cohort_id) return false;
            const cohort = cohorts.find((c: any) => c.id === r.cohort_id);
            return !!cohort && (cohort as any).created_by === user?.id;
          })
        : []);

  const getCohortName = (cohortId: string | null) => {
    if (!cohortId) return 'No cohort';
    const c = cohorts?.find((c: any) => c.id === cohortId);
    return (c as any)?.name || cohortId;
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold tracking-tight">Join Requests</h2>

      {!filteredRequests?.length ? (
        <p className="text-sm text-muted-foreground text-center py-8">No pending requests.</p>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map((req) => (
            <Card key={req.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">User: {req.user_id.slice(0, 8)}…</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{getCohortName(req.cohort_id)}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(req.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    disabled={manageMutation.isPending}
                    onClick={() => manageMutation.mutate({ action: 'approve_request', requestId: req.id })}
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={manageMutation.isPending}
                    onClick={() => manageMutation.mutate({ action: 'reject_request', requestId: req.id })}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
